import { isFundingMethod } from '../discovery/policy.js';
import { safeFundingDiagnostic } from './currency-diagnostics.js';
export const FUNDING_FUNNEL_STAGES = [
    'session',
    'discovery',
    'verification',
    'customer_verification',
    'methods',
    'profile',
    'amount',
    'card_entry',
    'card_validation',
    'card_quote',
    'card_deletion',
    'review',
    'payment',
    'bank_transfer_instructions',
    'challenge',
    'connection_required',
    'connect',
    'terms',
    'unavailable',
    'status',
    'email',
    'email_code',
    'phone',
    'phone_code',
    'consent',
    'contact_ready',
    'history',
    'receipt',
];
export const FUNDING_FUNNEL_OUTCOMES = ['loading', 'entered', 'ready', 'error'];
export const FUNDING_FUNNEL_ERRORS = [
    'none',
    'other',
    'provider_unavailable',
    'payment_result_timeout',
    'connection_frame_timeout',
    'connection_check_timeout',
    'connection_failed',
    'verification_required',
    'verification_rejected',
    'region_unavailable',
    'rate_limited',
    'invalid_request',
    'access_denied',
    'request_conflict',
    'code_invalid',
    'verification_failed',
    'verification_rate_limited',
    'sign_in',
    'submission_unknown',
    'quote_expired',
    'verification_pending',
    'cancelled',
    'rejected',
    'limit_blocked',
    'connection_required',
    'requote_required',
    'session_refresh_required',
    'onramp_refresh_failed',
];
/** Funnel observations never assert settlement and never carry provider payloads. */
export function createFundingTelemetry(options) {
    const stages = new Map();
    let disposed = false;
    let lastDiagnostic = null;
    let lastStage = null;
    let stageEnteredAt = Date.now();
    return {
        diagnose(diagnostic) {
            if (disposed || !options)
                return;
            if (!diagnostic) {
                lastDiagnostic = null;
                return;
            }
            const event = safeFundingDiagnostic(diagnostic);
            const key = JSON.stringify(event);
            if (key === lastDiagnostic)
                return;
            lastDiagnostic = key;
            try {
                options.onDiagnostic?.(event);
            }
            catch {
                try {
                    options.onFailure?.('onramp_telemetry_failed');
                }
                catch {
                    /* Diagnostics must not interrupt a payment. */
                }
            }
        },
        observe(observation) {
            if (!options || disposed)
                return;
            if (!FUNDING_FUNNEL_STAGES.includes(observation.stage) ||
                !FUNDING_FUNNEL_OUTCOMES.includes(observation.outcome))
                return;
            const event = project(observation, options.surface);
            const fingerprint = JSON.stringify(event);
            const previous = stages.get(observation.stage);
            if (lastStage === observation.stage && previous?.fingerprint === fingerprint)
                return;
            const now = Date.now();
            const startedAt = observation.outcome === 'loading' ? now : (previous?.startedAt ?? now);
            if (lastStage !== observation.stage) {
                if (lastStage) {
                    event.previousStage = lastStage;
                    event.previousStageElapsedMs = Math.min(3600000, Math.max(0, now - stageEnteredAt));
                }
                stageEnteredAt = now;
            }
            stages.set(observation.stage, { fingerprint, startedAt });
            lastStage = observation.stage;
            event.elapsedMs = Math.min(3600000, Math.max(0, now - startedAt));
            try {
                options.emit(event);
            }
            catch {
                try {
                    options.onFailure?.('onramp_telemetry_failed');
                }
                catch {
                    /* A diagnostic sink must not interrupt a payment. */
                }
            }
        },
        dispose() {
            disposed = true;
            stages.clear();
        },
    };
}
function project(value, surface) {
    const code = value.errorCode;
    const errorCode = !code
        ? 'none'
        : (FUNDING_FUNNEL_ERRORS.find((item) => item === code) ?? 'other');
    const operationId = value.operationId;
    return {
        schemaVersion: 1,
        provider: 'moonpay',
        surface,
        stage: value.stage,
        outcome: value.outcome,
        method: value.method && isFundingMethod(value.method) ? value.method : 'none',
        errorCode,
        elapsedMs: 0,
        ...(operationId &&
            /^[a-f\d]{8}-[a-f\d]{4}-[1-8][a-f\d]{3}-[89ab][a-f\d]{3}-[a-f\d]{12}$/i.test(operationId)
            ? { operationId }
            : {}),
    };
}
